import { DualSelect } from '@demandlocal/components'
import { motion } from 'framer-motion'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { ReactComponent as RemoveIcon } from '../../../../images/icons/remove.svg'
import { useGetVendorAdAccounts } from '../../../../api/dealership/dsp/adAccounts/useGetVendorAdAccounts'
import { VENDOR } from '../../../../lib/utils/vendors'
import { useRefetchVendorAdAccounts } from '../../../../api/dealership/dsp/adAccounts/useRefetchVendorAdAccounts'
import toast from 'react-hot-toast'

export default function DealershipInfo(): JSX.Element {
  const { setValue, watch, control, getValues } = useFormContext()
  const assignedGA4Accounts = watch('ga4_accounts')

  const { data } = useGetVendorAdAccounts({
    vendor_id: VENDOR.GoogleAnalytics,
    force_refresh: false,
  })

  const {
    mutateAsync: refetchVendorAdAccounts,
    isLoading: isRefetchingVendorAdAccounts,
  } = useRefetchVendorAdAccounts()

  const vendorAccountsOptions = useMemo(() => {
    return data?.length > 0
      ? data.map((v) => ({
          label: `${v?.name ?? v?.label}`,
          value: v?.id ?? v?.value,
        }))
      : []
  }, [data])

  return (
    <div className={'pt-8 pb-8 flex flex-col gap-5'}>
      <div className={'flex flex-col pr-5'}>
        <label
          className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
          htmlFor={'organizations'}>
          Assign GA4 Accounts
        </label>
        <div className="flex flex-col gap-2">
          <div className={'flex flex-shrink-0 mt-4'}>
            <DualSelect
              optionsListTitle={'Choose GA4 accounts'}
              selectedOptionsListTitle={'Selected accounts'}
              buttonTitle={'+ Assign account'}
              control={control}
              options={vendorAccountsOptions}
              name={`ga4_accounts`}
            />
            {assignedGA4Accounts?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className={
                  'pl-5 ml-2 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                }>
                {assignedGA4Accounts?.map((value: { value: number; label: string }) => {
                  return (
                    <div
                      key={value.value}
                      className={
                        'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                      }>
                      <div className={'flex justify-between text-xs'}>
                        <span className={'font-bold'}>{value.label}</span>
                        <RemoveIcon
                          className={'cursor-pointer ml-2'}
                          onClick={(e) => {
                            e.preventDefault()
                            setValue(
                              `ga4_accounts`,
                              getValues(`ga4_accounts`).filter(
                                (v: { value: number }) => v.value !== value.value,
                              ),
                            )
                          }}
                        />
                      </div>
                      <span className={'font-medium pt-1 text-xs'}>{value.value}</span>
                    </div>
                  )
                })}
              </motion.div>
            )}
          </div>
          <div className={'font-medium flex flex-row mt-2 text-xs text-web-gray'}>
            Can't see your account?{' '}
            <span
              onClick={async (event) => {
                event.preventDefault()
                if (isRefetchingVendorAdAccounts) return
                const toastId = toast.loading('Refreshing list... This may take a while.')
                await refetchVendorAdAccounts({
                  vendor_id: VENDOR.GoogleAnalytics,
                }).catch((err) => {
                  toast.error('Something went wrong. Please try again later.', {
                    id: toastId,
                  })
                })
                toast.success('List refreshed successfully!', { id: toastId })
              }}
              className={`${
                isRefetchingVendorAdAccounts
                  ? 'text-gainsboro-gray cursor-not-allowed'
                  : 'text-primary-persian-blue cursor-pointer hover:underline'
              } ml-2`}>
              Refresh list
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
